import { useMutation, useQuery } from "@tanstack/react-query";
import {
  MaterialReactTable,
  MRT_PaginationState,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
} from "material-react-table";
import React, { useState, useEffect, useRef } from "react";
import CampaignsService from "../service/campaigns.service";
import { CampaignState } from "../service/campaigns.types";
import moment from "moment";
import {
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Avatar
} from "@mui/material";
import { AuthenticatedImageBackgroundDiv } from "modules/UI/components/AuthenticatedImage.component";
import CampaignMaterialForm from "../components/CampaignMaterialForm.component";
import CreateCampaignContainer from "./create.container";
import Drawer from "modules/UI/components/Drawer.component";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { useActiveUser } from "modules/User/activeUser";
import {
  Add,
  Download,
  Search,
  Upload,
} from "@mui/icons-material";
import saveAs from "file-saver";
import { CampaignStateColumnFilter } from "../components/CampaignStateColumnFilter.component";
import { useUserPermissions } from "modules/User/service/userPermissions.hook";

const isPendingState = (state: CampaignState): boolean => {
  return (
    state === CampaignState.Pending_NPD ||
    state === CampaignState.Pending_GlobalQuality ||
    state === CampaignState.Pending_Rejected_NPD ||
    state === CampaignState.Pending_Market_Proc ||
    state === CampaignState.Pending_Rejected_GlobalQuality ||
    state === CampaignState.Pending_Confirm_Price
  );
};

export const CampaignsMaintenanceContainer = (props: {
  onImport: () => void;
}) => {
  const initializedRef = useRef(false);
  const [drawerInitialData, setDrawerInitialData] = useState(null);
  const activeUser = useActiveUser();
  const { data: userPermissions } = useUserPermissions();

  const permissions =
    userPermissions?.permissions?.map((p) => p.name) ?? [];

  const allowedOrderWindows =
    userPermissions?.allowedOrderWindows?.map((p) => p.name) ?? [];
  const stateMap: Record<string, CampaignState> = {
    Draft: CampaignState.Draft,
    Active: CampaignState.Active,
    Future: CampaignState.Future,
    Past: CampaignState.Past,
    Archived: CampaignState.Archived,
    Pending_NPD: CampaignState.Pending_NPD,
    Pending_GlobalQuality: CampaignState.Pending_GlobalQuality,
    Pending_Rejected_NPD: CampaignState.Pending_Rejected_NPD,
    Pending_Market_Proc: CampaignState.Pending_Market_Proc,
    Pending_Rejected_GlobalQuality:
      CampaignState.Pending_Rejected_GlobalQuality,
    Pending_Confirm_Price:
      CampaignState.Pending_Confirm_Price,
    Closed_Submit_PR:
      CampaignState.Closed_Submit_PR,
  };
  const defaultStates: CampaignState[] = allowedOrderWindows
    .map((name) => stateMap[name])
    .filter(
      (state): state is CampaignState =>
        state !== undefined
    );
  const hasPermission = (permission: string) =>
    permissions.includes(permission);

  const hasAllowedOrderWindow = (state: string) =>
    allowedOrderWindows.includes(state);

  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [stateFilter, setStateFilter] = useState<CampaignState[]>([]);
  useEffect(() => {
    if (initializedRef.current) return;
    if (!defaultStates.length) return;

    setStateFilter(defaultStates);

    initializedRef.current = true;
  }, [defaultStates]);


  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const {
    data: allCampaigns,
    refetch: refetchAllCampaigns,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: [
      "campaigns/all",
      searchText,
      stateFilter,
      pagination.pageIndex,
      pagination.pageSize,
    ],
    queryFn: async () => {
      // const response = await CampaignsService.getCampaigns({
      //   searchTerm: searchText,
      //   state: stateFilter !== null && stateFilter !== undefined ? stateFilter : undefined,
      //   take: pagination.pageSize,
      //   skip: pagination.pageIndex * pagination.pageSize,
      // });
      console.log("stateFilter", stateFilter);
      const response = await CampaignsService.getCampaigns({
        searchTerm: searchText,
        state: stateFilter,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
      });
      return response;
    },
    // initialData: { data: [], take: 100, skip: 0, total: 0 },
  });
  const { mutateAsync: archiveCampaign, isPending: archivingCampaign } =
    useMutation<any, any, { campaignId: string | number }>({
      mutationKey: ["archiveCampaign"],
      mutationFn: async ({ campaignId }: { campaignId: string | number }) => {
        await CampaignsService.archiveCampaign({ campaignId });
      },
      onSuccess: () => {
        toast.success("Campaign archived successfully!");
        refetchAllCampaigns();
      },
    });
  const { mutateAsync: publishCampaign, isPending: publishingCampaign } =
    useMutation({
      mutationKey: ["publishCampaign"],
      mutationFn: async ({ campaignId }: { campaignId: string | number }) => {
        await CampaignsService.publishCampaign({ campaignId });
        return campaignId;
      },
      onSuccess: () => {
        toast.success("Campaign published successfully!");
        refetchAllCampaigns();
      },
    });

  const { mutate: exportMaterials, isPending: exportingCampaigns } =
    useMutation({
      mutationKey: ["catalog-export-materials"],
      mutationFn: async () => {
        // export logic here
        const response = await CampaignsService.exportCampaign({
          filters: null,
          searchTerm: searchText,
        });
        saveAs(response, "campaigns.xlsx");
      },
      onSuccess: () => {
        toast.success("Campaigns exported successfully");
      },
    });

  return (
    <>
      <Drawer
        title="Edit Campaign"
        open={!!drawerInitialData}
        onClose={() => setDrawerInitialData(null)}
      >
        {!!drawerInitialData && (
          <CreateCampaignContainer
            onClose={() => {
              setDrawerInitialData(null);
              refetchAllCampaigns();
            }}
            initialData={drawerInitialData}
          />
        )}
      </Drawer>
      <MaterialReactTable
        state={{
          isLoading,
          pagination,
          showProgressBars: isRefetching,
        }}
        onPaginationChange={setPagination}
        manualPagination={true}
        enableFilters={true}
        enableColumnFilters={true}
        enableRowActions
        enableColumnActions={false}
        enableColumnDragging={false}
        enableColumnResizing={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableStickyHeader
        layoutMode="grid-no-grow"
        // muiTableContainerProps={{
        //   sx: {
        //     overflowX: "auto",
        //     maxWidth: "100%",
        //   },
        // }}
        muiTableContainerProps={{
          sx: {
             maxHeight: "calc(100vh - 320px)",
    overflow: "auto",
          },
        }}
        muiTableProps={{
          sx: {
            tableLayout: "fixed",
            width: "2400px",
            minWidth: "2400px",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 600,
            alignItems: "center",
            justifyContent: "flex-start",
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            alignItems: "center",
          },
        }}
        renderRowActionMenuItems={({ row }) => {
          return [
            <MenuItem
              onClick={() => setDrawerInitialData(row.original)}
              key={row.id + "-edit"}
              disabled={!hasPermission("Campaign.Edit")}
            >
              Edit
            </MenuItem>,
            <MenuItem
              onClick={() => setDrawerInitialData(row.original)}
              key={row.id + "-publish"}
              disabled={
                publishingCampaign ||
                !hasPermission("Campaign.Publish")
              }
            >
              Publish Campaign
            </MenuItem>,
            <MenuItem
              onClick={() => setDrawerInitialData(row.original)}
              key={row.id + "-archive"}
              disabled={
                archivingCampaign ||
                row.original.isDeleted ||
                !hasAllowedOrderWindow("Archived")
              }
            >
              Archive Campaign
            </MenuItem>,
          ];
        }}
        initialState={{
          pagination: {
            pageIndex: 0,
            pageSize: 100,
          },
          columnPinning: {
            left: ["mrt-row-expand"],
            right: ["mrt-row-actions"],
          },
        }}
        renderTopToolbarCustomActions={() => (
          <>

            <ButtonGroup color="inherit">
              <Button
                onClick={() => navigate("/campaigns/create/new")}
                startIcon={<Add />}
                disabled={!hasPermission("Campaign.Create")}
              >
                New
              </Button>

              {props.onImport && (
                <Button
                  onClick={() => props.onImport()}
                  startIcon={<Upload />}
                  disabled={!hasPermission("Campaign.Create")}
                >
                  Import
                </Button>
              )}

              <Button
                onClick={() => exportMaterials()}
                startIcon={
                  exportingCampaigns ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Download />
                  )
                }
              >
                Export
              </Button>
            </ButtonGroup>

          </>
        )}
        renderToolbarInternalActions={({ table }) => (
          <>
            <TextField
              size="small"
              placeholder="Start typing to search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => { }} edge="end">
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        mrtTheme={{
          baseBackgroundColor: "#fff",
        }}
        columns={[
          {
            accessorKey: "id",
            header: "ID",
            size: 120,
            grow: false,
            Cell: ({ row }) => (
              <Tooltip title={row.original.idWithPrefix || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Link to={`/campaigns/${row.original.id}`}>
                    {row.original.idWithPrefix}
                  </Link>
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "mediaFiles",
            header: "Image",
            size: 120,
            grow: false,
            enableSorting: false,
            Cell: ({ row }) => {
              const firstPhotoUrl = row.original.mediaFiles?.[0]?.url;

              return firstPhotoUrl ? (
                <AuthenticatedImageBackgroundDiv
                  src={firstPhotoUrl}
                  style={{ width: 56, height: 56, borderRadius: 4 }}
                />
              ) : (
                <Avatar variant="rounded" sx={{ width: 56, height: 56 }} />
              );
            },
          },
          {
            accessorKey: "name",
            header: "Name",
            size: 220,
            grow: false,
          },
          {
            accessorKey: "description",
            header: "About",
            size: 220,
            grow: false,
          },
          {
            accessorKey: "state",
            header: "Status",
            size: 220,
            grow: false,
            enableColumnFilter: true,
            filterFn: () => true,
            Cell: ({ row }) => {
              const stateLabels = [
                "Draft",
                "Active",
                "Upcoming",
                "Past",
                "Archived",
                "Pending NPD",
                "Pending - Global Quality",
                "Pending - Rejected NPD",
                "Pending - Market Proc",
                "Pending - Rejected Global Quality",
                "Pending - Confirm Price",
              ];

              return (
                <Tooltip
                  title={
                    stateLabels[row.original.state] || row.original.state
                  }
                  arrow
                >
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stateLabels[row.original.state] || row.original.state}
                  </div>
                </Tooltip>
              );
            },
            Filter: ({ column }) => {
              return (
                <CampaignStateColumnFilter
                  column={column}
                  allowedOrderWindows={allowedOrderWindows}
                  // onStateChange={(state) => {
                  //   setStateFilter(state);
                  //   setPagination({
                  //     ...pagination,
                  //     pageIndex: 0,
                  //   });
                  // }}
                  onStateChange={(state) => {
                    setStateFilter(
                      state.length === 0 ? defaultStates : state
                    );

                    setPagination({
                      ...pagination,
                      pageIndex: 0,
                    });
                  }}
                  currentState={stateFilter}
                />
              );
            },
          },
          {
            accessorKey: "parentPromoItemId",
            header: "Catalog Number",
            size: 180,
            grow: false,
          },
          // {
          //   accessorKey: "startDate",
          //   header: "Start Date",
          //   size: 180,
          //   grow: false,
          //   Cell: ({ row }) => (
          //     <>
          //       {moment(row.original.startDate).format("DD/MM/YYYY")}
          //       <br />
          //       ({moment(row.original.startDate).fromNow()})
          //     </>
          //   ),
          // },
          // {
          //   accessorKey: "endDate",
          //   header: "End Date",
          //   size: 180,
          //   grow: false,
          //   Cell: ({ row }) => (
          //     <>
          //       {moment(row.original.endDate).format("DD/MM/YYYY")}
          //       <br />
          //       ({moment(row.original.endDate).fromNow()})
          //     </>
          //   ),
          // },
          // {
          //   accessorKey: "validTo",
          //   header: "Prices Valid Until",
          //   size: 220,
          //   grow: false,
          //   Cell: ({ row }) => (
          //     <>
          //       {moment(row.original.validTo).format("DD/MM/YYYY")}
          //       <br />
          //       ({moment(row.original.validTo).fromNow()})
          //     </>
          //   ),
          // },
          {
            accessorKey: "startDate",
            header: "Start Date",
            size: 180,
            grow: false,
            Cell: ({ row }) => {
              const value = `${moment(row.original.startDate).format(
                "DD/MM/YYYY"
              )} (${moment(row.original.startDate).fromNow()})`;

              return (
                <Tooltip title={value} arrow>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {value}
                  </div>
                </Tooltip>
              );
            },
          },
          {
            accessorKey: "endDate",
            header: "End Date",
            size: 180,
            grow: false,
            Cell: ({ row }) => {
              const value = `${moment(row.original.endDate).format(
                "DD/MM/YYYY"
              )} (${moment(row.original.endDate).fromNow()})`;

              return (
                <Tooltip title={value} arrow>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {value}
                  </div>
                </Tooltip>
              );
            },
          },
          {
            accessorKey: "validTo",
            header: "Prices Valid Until",
            size: 220,
            grow: false,
            Cell: ({ row }) => {
              const value = `${moment(row.original.validTo).format(
                "DD/MM/YYYY"
              )} (${moment(row.original.validTo).fromNow()})`;

              return (
                <Tooltip title={value} arrow>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {value}
                  </div>
                </Tooltip>
              );
            },
          },
          {
            accessorKey: "incoTerms",
            header: "Incoterms",
            size: 140,
            grow: false,
          },
          {
            accessorKey: "materialsCount",
            header: "Materials Count",
            size: 180,
            grow: false,
          },
          {
            accessorKey: "markets",
            header: "Scope",
            size: 250,
            grow: false,
            Cell: ({ row }) => (
              <Tooltip
                title={row.original.markets
                  .map((market) => market.name)
                  .join(", ")}
                arrow
              >
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.original.markets
                    .map((market) => market.name)
                    .join(", ")}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "supplierName",
            header: "Supplier",
            size: 220,
            grow: false,
          },
          {
            accessorKey: "supplierContact.email",
            header: "Supplier Contact",
            size: 260,
            grow: false,
          },
          {
            accessorKey: "supplierContact.username",
            header: "Supplier Name",
            size: 220,
            grow: false,
            Cell: ({ row }) => row.original.supplierContact?.username || "-",
          },
        ]}
        data={allCampaigns?.data || []}
        rowCount={allCampaigns?.total || 0}
        renderDetailPanel={({ row }) => (
          <CampaignMaterialsListContainer
            campaignId={row.original.id}
          />
        )}
      />
    </>
  );
};

const CampaignMaterialsListContainer = (props: {
  campaignId: string | number;
}) => {
  const [drawerInitialData, setDrawerInitialData] = useState(null);
  const {
    data: { data: materials },
    refetch: refetchMaterials,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["materials", props.campaignId],
    queryFn: async () => {
      return await CampaignsService.getCampaignMaterials({
        orderWindowId: parseInt(props.campaignId.toString()),
        take: 100,
        skip: 0,
      });
    },

    initialData: {
      data: [],
      take: 100,
      skip: 0,
      total: 0,
    },
  });
  return (
    <>
      <Drawer
        open={!!drawerInitialData}
        onClose={() => setDrawerInitialData(null)}
        title="Product details"
      >
        <CampaignMaterialForm
          initialValues={drawerInitialData}
          onClose={() => {
            setDrawerInitialData(null);
            refetchMaterials();
          }}
        />
      </Drawer>
      <MaterialReactTable
        state={{
          isLoading,
          showProgressBars: isRefetching,
        }}
        layoutMode="semantic"
        enableColumnResizing={false}
        enableStickyHeader
        enableColumnActions={false}
        enableColumnDragging={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableSorting
        // muiTableContainerProps={{
        //   sx: {
        //     overflowX: "auto",
        //     maxWidth: "100%",
        //   },
        // }}
        muiTableContainerProps={{
          sx: {
             maxHeight: "calc(100vh - 320px)",
    overflow: "auto",
          },
        }}
        // muiTableProps={{
        //   sx: {
        //     tableLayout: "fixed",
        //     width: "100%",
        //     minWidth: "2200px",
        //   },
        // }}
        muiTableProps={{
          sx: {
            tableLayout: "auto",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 600,
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "0px",
          },
        }}
        initialState={{
          isLoading,
          pagination: {
            pageIndex: 0,
            pageSize: 100,
          },
          columnPinning: {
            left: ["mrt-row-expand"],
          },
        }}
        mrtTheme={{
          baseBackgroundColor: "#fff",
        }}
        columns={[
          {
            accessorKey: "id",
            header: "ID",
            size: 120,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "promoCategoryId",
            header: "Sub Category",
            size: 180,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "materialName",
            header: "Material Name",
            size: 220,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "shortDescription",
            header: "Short Description",
            size: 220,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "itemDescription",
            header: "Scope of Application",
            size: 250,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "supplierPartId",
            header: "Supplier Part ID",
            size: 180,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "manufacturerName",
            header: "Manufacturer Name",
            size: 220,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "manufacturerPartNumber",
            header: "Manufacturer Part Number",
            size: 220,
          },
          {
            accessorKey: "supplierLeadTime",
            header: "Supplier Lead Time",
            size: 180,
          },
          {
            accessorKey: "currencyCode",
            header: "Currency",
            size: 120,
          },
          {
            accessorKey: "unitOfMeasure",
            header: "Unit Of Measure",
            size: 180,
          },
          {
            accessorKey: "minimumOrderQuantity",
            header: "Minimum Order Quantity",
            size: 220,
          },
          {
            accessorKey: "minimumPurchaseQuantity",
            header: "Minimum Pack Quantity",
            size: 220,
          },
          {
            accessorKey: "priceRanges",
            header: "Price Range",
            size: 220,
            Cell: ({ row }) => (
              <Tooltip
                arrow
                title={
                  <>
                    {[...row.original.priceRanges]
                      .sort((a, b) => a.threshold - b.threshold)
                      .map((range, index) => (
                        <div key={index}>
                          {range.threshold}+ - {range.unitPrice}{" "}
                          {row.original.currencyCode}
                        </div>
                      ))}
                  </>
                }
              >
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {Math.min(
                    ...row.original.priceRanges.map((x) => x.unitPrice)
                  )}{" "}
                  -{" "}
                  {Math.max(
                    ...row.original.priceRanges.map((x) => x.unitPrice)
                  )}{" "}
                  {row.original.currencyCode}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "keyWords",
            header: "Keywords",
            size: 220,
            Cell: ({ cell }) => (
              <Tooltip title={cell.getValue<string>() || ""} arrow>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {cell.getValue<string>()}
                </div>
              </Tooltip>
            ),
          },
          {
            accessorKey: "languageId",
            header: "Language",
            size: 140,
          },
        ]}
        data={materials || []}
      />
    </>
  );
};

export default CampaignsMaintenanceContainer;
